import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { ReplayRow } from "./use-enforcement.ts";
import {
  deriveReplayViews,
  quantityMeasure,
  quantityValue,
  replayNamespaceLabel,
  replayHref,
  type ReplayView,
} from "./vocab.ts";

/**
 * Replay detections by namespace (task 11.8; design 12.2 "replay detections by namespace", FR-5.2).
 *
 * THE ALWAYS-PRESENT-WITH-ZEROS GUARANTEE. The replay breakdown is reconciled against the frozen
 * six-namespace catalog by `deriveReplayViews`, so this table ALWAYS renders one row per namespace —
 * `operation_proof`, `gateway_assertion`, `identity_token`, `decision`, `enrollment`, `other` — even
 * when a namespace saw no replay detections, where it shows an explicit 0 (not a hidden or dropped
 * row). The contract already zero-fills the server payload to the catalog; the derivation keeps the
 * presentation complete and ordered regardless. An all-zero table is the legitimate "no replay
 * detections" state shown as zeros — a healthy READY view, NOT an empty/collapsed state (FR-5.2).
 *
 * Each namespace drills to the replay events behind it (`category=replay`, namespace). Counts carry
 * their exact/estimate/unverifiable confidence (design 12.3); a non-zero count is a warning the
 * operator should see (icon + text via `StatusBadge`), a zero is neutral, and a null is grey
 * `unknown` — never green (design 12.3 / 12.4).
 */

export interface ReplayByNamespaceProps {
  readonly rows: readonly ReplayRow[];
}

export function ReplayByNamespace(props: ReplayByNamespaceProps): ReactNode {
  const navigate = useNavigate();
  // Catalog-complete rows: one per namespace, zero-filled. Never dropped.
  const views = deriveReplayViews(props.rows);
  const anyDetections = views.some((v) => quantityValue(v.count) > 0);

  const columns: readonly Column<ReplayView>[] = [
    {
      key: "namespace",
      header: "Namespace",
      isRowHeader: true,
      cell: (v) => {
        const value = quantityValue(v.count);
        const nullCount = v.count.value === null;
        const level = nullCount ? "unknown" : value > 0 ? "warn" : "neutral";
        return (
          <span className="enf-row-key">
            <StatusBadge
              level={level}
              label={replayNamespaceLabel(v.namespace)}
              size="sm"
              title={
                nullCount
                  ? `Replay detections for ${replayNamespaceLabel(
                      v.namespace,
                    )} could not be verified in this window.`
                  : value > 0
                    ? `${value} replay detection(s) in ${replayNamespaceLabel(v.namespace)}.`
                    : `No replay detections in ${replayNamespaceLabel(v.namespace)}.`
              }
            />
          </span>
        );
      },
    },
    {
      key: "count",
      header: "Detections",
      align: "right",
      cell: (v) => {
        const measure = quantityMeasure(v.count);
        const text =
          v.count.value === null
            ? "n/a"
            : `${defaultNumberFormat(v.count.value)}${estimateSuffix(measure)}`;
        return (
          <button
            type="button"
            className="enf-link enf-num"
            data-measure={measure}
            onClick={() => navigate(replayHref(v.namespace))}
            aria-label={`Open the replay events for ${replayNamespaceLabel(
              v.namespace,
            ).toLowerCase()}`}
          >
            {text}
          </button>
        );
      },
    },
  ];

  return (
    <section className="enf-section" aria-label="Replay detections by namespace">
      <div className="enf-section__head">
        <h2 className="enf-section__title">Replay detections by namespace</h2>
        <span className="enf-section__note">
          {anyDetections
            ? "Replay detections were reported in this window."
            : "No replay detections. Every namespace is shown with zero."}
        </span>
      </div>
      {/* `rows` is never empty here (one per catalog namespace), so `emptyMessage` is a safeguard
          only; the all-zero case renders real zero rows, not this message. */}
      <DenseTable
        caption="Replay detections by namespace, one row per namespace, zero when none were detected"
        columns={columns}
        rows={views}
        rowKey={(v) => v.namespace}
        emptyMessage="The replay namespaces could not be loaded for this window."
      />
    </section>
  );
}
